import { useState, useCallback, useEffect, useRef } from 'react';
import { Box, LoadingOverlay, Paper, Transition, Tabs, Text } from '@mantine/core';
import { User } from '@propelauth/react';
import chartPieIcon from '../assets/icons/chart-pie.svg';
import walletIcon from '../assets/icons/wallet.svg';
import { useBudgetPlan } from '../hooks/useBudgetPlan';
import BudgetDisplay from './BudgetDisplay';
import BudgetCategories from './BudgetCategories';
import * as Sentry from '@sentry/react';

interface BudgetTabProps {
    accessToken: string;
    user: User;
    isVisible?: boolean;
}

const BudgetTab = ({ accessToken, user, isVisible = true }: BudgetTabProps) => {
    const [activeView, setActiveView] = useState<string>('overview'); // Default to 'overview'
    const prevPlanIdRef = useRef<number | undefined>(undefined);

    const {
        plan,
        loading,
        error,
        refreshPlan
    } = useBudgetPlan(accessToken, { isVisible });

    useEffect(() => {
        // This useEffect for logging plan changes can remain as is.
        console.log('[BudgetTab] Plan object updated:', plan);
        if (plan && plan.id !== prevPlanIdRef.current) {
            console.log(`[BudgetTab] Plan ID changed from ${prevPlanIdRef.current} to ${plan.id}. Full new plan:`, JSON.parse(JSON.stringify(plan)));
            prevPlanIdRef.current = plan.id;
        } else if (plan) {
            console.log('[BudgetTab] Plan object updated, but ID is the same:', plan.id);
        } else {
            console.log('[BudgetTab] Plan is now null or undefined.');
        }
    }, [plan]);

    const handlePlanUpdate = useCallback(() => {
        refreshPlan();
    }, [refreshPlan]);

    if (error) {
        Sentry.captureMessage(`Error loading budget plan: ${error}`);
        // Optionally, render an error state in the UI
    }

    return (
        <Box className="w-full max-w-6xl mx-auto">
            <Paper radius="md" p={0} withBorder style={{ overflow: 'hidden' }}>
                <Box style={{ position: 'relative' }}>
                    {/* Show loading overlay if loading AND no plan data yet (initial load) */}
                    <LoadingOverlay visible={loading && !plan} />

                    <Tabs
                        value={activeView}
                        onChange={(value) => setActiveView(value as string)}
                        variant="outline"
                    >
                        <Tabs.List bg="rgba(0,0,0,0.03)" p="xs">
                            <Tabs.Tab
                                value="overview"
                                leftSection={
                                    <img
                                        src={chartPieIcon}
                                        alt="Overview"
                                        style={{ width: '0.9rem', height: '0.9rem', opacity: 0.7 }}
                                    />
                                }
                            >
                                Overview
                            </Tabs.Tab>
                            <Tabs.Tab
                                value="categories"
                                leftSection={
                                    <img
                                        src={walletIcon}
                                        alt="Categories"
                                        style={{ width: '0.9rem', height: '0.9rem', opacity: 0.7 }}
                                    />
                                }
                            >
                                Categories
                            </Tabs.Tab>
                        </Tabs.List>

                        <Box py="md">
                            {/* Overview Tab Content */}
                            {activeView === 'overview' && plan && (
                                <Transition mounted={!!plan} transition="fade" duration={200}>
                                    {(styles) => (
                                        <div style={styles}>
                                            <BudgetDisplay
                                                budgetSummary={plan} // plan is BudgetSummary | null
                                                accessToken={accessToken}
                                                currentUser={user}
                                                onUpdate={handlePlanUpdate}
                                            />
                                        </div>
                                    )}
                                </Transition>
                            )}
                            {activeView === 'overview' && !plan && !loading && (
                                <Box p="md"><Text>No budget plan found. One will be created for you, or you can manage categories.</Text></Box>
                            )}

                            {/* Categories Tab Content */}
                            {activeView === 'categories' && plan && (
                                <Transition mounted={!!plan} transition="fade" duration={200}>
                                    {(styles) => (
                                        <div style={styles}>
                                            <BudgetCategories
                                                budgetSummary={plan} // plan is BudgetSummary | null
                                                accessToken={accessToken}
                                                onUpdate={handlePlanUpdate}
                                            />
                                        </div>
                                    )}
                                </Transition>
                            )}
                            {activeView === 'categories' && !plan && !loading && (
                                <Box p="md"><Text>No budget plan found. Manage categories here once a plan is available.</Text></Box>
                            )}
                        </Box>
                    </Tabs>
                </Box>
            </Paper>
        </Box>
    );
};

export default BudgetTab; 